import {
  BadgeCheck,
  CheckCircle2,
  ChevronRight,
  CircleAlert,
  KeyRound,
  Settings2,
  Shield,
  WalletCards,
  LockKeyhole,
  ChevronUp,
  ChevronDown,
} from "lucide-react";
import { useNavigate } from "react-router-dom";

import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { Card, CardContent } from "../ui/card";
import { cn } from "@/lib/utils";
import { useState } from "react";
import MerchantSetting from "./MerchantSetting";

export type MerchantRequirementKey =
  | "kyc"
  | "merchantSettings"
  | "twoFactor"
  | "transactionPassword"
  | "paymentMethod";

export interface MerchantRequirements {
  kyc: boolean;
  merchantSettings: boolean;
  twoFactor: boolean;
  transactionPassword: boolean;
  paymentMethod: boolean;
}

interface P2PGateProps {
  requirements: MerchantRequirements;
  userId: string;
}

interface RequirementItem {
  key: MerchantRequirementKey;
  title: string;
  description: string;
  completedDescription: string;
  icon: React.ElementType;
  route: string;
  actionLabel: string;
}

const P2PGate = ({ requirements, userId }: P2PGateProps) => {
  const navigate = useNavigate();

  const [showMerchantSettings, setShowMerchantSettings] = useState(false);

  const requirementItems: RequirementItem[] = [
    {
      key: "kyc",
      title: "Identity Verification",
      description:
        "Complete identity verification before you can participate as a P2P merchant.",
      completedDescription: "Your identity has been successfully verified.",
      icon: BadgeCheck,
      route: "/dashboard/kyc",
      actionLabel: "Complete KYC",
    },
    {
      key: "merchantSettings",
      title: "Merchant Settings",
      description:
        "Configure your merchant profile, default fiat currency, pricing preferences and trade settings.",
      completedDescription:
        "Your merchant profile and trading preferences are configured.",
      icon: Settings2,
      route: "/dashboard/p2p?tab=settings",
      actionLabel: "Configure Settings",
    },
    {
      key: "twoFactor",
      title: "Two-Factor Authentication",
      description:
        "Protect your merchant account with two-factor authentication before handling P2P trades.",
      completedDescription:
        "Two-factor authentication is enabled on your account.",
      icon: Shield,
      route: "/dashboard/settings?tab=security",
      actionLabel: "Enable 2FA",
    },
    {
      key: "transactionPassword",
      title: "Transaction Password",
      description:
        "Set a dedicated transaction password to authorize sensitive merchant and trading operations.",
      completedDescription: "Your transaction password has been configured.",
      icon: KeyRound,
      route: "/dashboard/settings?tab=security",
      actionLabel: "Set Transaction Password",
    },
    {
      key: "paymentMethod",
      title: "Verified Payment Method",
      description:
        "Add and verify at least one payment method that customers can use to complete P2P trades.",
      completedDescription:
        "At least one verified payment method is available.",
      icon: WalletCards,
      route: "/dashboard/settings?tab=payment-methods",
      actionLabel: "Add Payment Method",
    },
  ];

  const completedCount = requirementItems.filter(
    (item) => requirements[item.key],
  ).length;

  const totalCount = requirementItems.length;
  const progressPercentage = Math.round((completedCount / totalCount) * 100);

  const allCompleted = completedCount === totalCount;

  const firstIncompleteRequirement = requirementItems.find(
    (item) => !requirements[item.key],
  );

  const handleRequirementAction = (route: string) => {
    navigate(route);
  };

  return (
    <div className="min-h-screen bg-background px-4 py-8 sm:px-6 lg:px-8">
      <div className="mx-auto flex min-h-[calc(100vh-4rem)] w-full max-w-4xl items-center justify-center">
        <Card className="relative w-full overflow-hidden rounded-[2rem] border-border/60 bg-card shadow-xl">
          {/* Decorative background effects */}
          <div className="pointer-events-none absolute -right-32 -top-32 h-80 w-80 rounded-full bg-primary/[0.08] blur-3xl" />

          <div className="pointer-events-none absolute -bottom-32 -left-32 h-80 w-80 rounded-full bg-emerald-500/[0.07] blur-3xl" />

          <CardContent className="relative p-5 sm:p-8 lg:p-10">
            {/* Header */}
            <div className="flex flex-col gap-5 sm:flex-row sm:items-start">
              <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl border border-primary/10 bg-primary/10">
                <Shield className="h-7 w-7 text-primary" />
              </div>

              <div className="flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <h1 className="text-2xl font-bold tracking-tight sm:text-3xl">
                    Merchant Access
                  </h1>

                  <Badge
                    variant={allCompleted ? "default" : "secondary"}
                    className={cn(
                      "rounded-full px-3 py-1",
                      allCompleted &&
                        "bg-emerald-500 text-white hover:bg-emerald-500",
                    )}
                  >
                    {allCompleted ? "Ready" : "Setup Required"}
                  </Badge>
                </div>

                <p className="mt-2 max-w-2xl text-sm leading-relaxed text-muted-foreground sm:text-base">
                  Complete your merchant setup to unlock P2P trading. We require
                  these security and account checks to protect you and other
                  traders on the platform.
                </p>
              </div>
            </div>

            {/* Progress */}
            <div className="mt-8 rounded-2xl border border-border/60 bg-muted/30 p-5 sm:p-6">
              <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <p className="text-sm font-medium text-muted-foreground">
                    Merchant readiness
                  </p>

                  <div className="mt-1 flex items-baseline gap-2">
                    <span className="text-3xl font-bold tracking-tight">
                      {completedCount}
                    </span>

                    <span className="text-sm text-muted-foreground">
                      of {totalCount} requirements completed
                    </span>
                  </div>
                </div>

                <div className="text-left sm:text-right">
                  <p
                    className={cn(
                      "text-2xl font-bold",
                      allCompleted ? "text-emerald-500" : "text-primary",
                    )}
                  >
                    {progressPercentage}%
                  </p>

                  <p className="text-xs text-muted-foreground">
                    Setup progress
                  </p>
                </div>
              </div>

              <div className="mt-5 h-2 overflow-hidden rounded-full bg-muted">
                <div
                  className={cn(
                    "h-full rounded-full transition-all duration-500",
                    allCompleted ? "bg-emerald-500" : "bg-primary",
                  )}
                  style={{
                    width: `${progressPercentage}%`,
                  }}
                />
              </div>
            </div>

            {/* Requirements */}
            <div className="mt-8">
              <div className="mb-4">
                <h2 className="text-lg font-semibold">Merchant requirements</h2>

                <p className="mt-1 text-sm text-muted-foreground">
                  All requirements must be completed before merchant access is
                  enabled.
                </p>
              </div>

              <div className="space-y-3">
                {requirementItems.map((item, index) => {
                  const completed = requirements[item.key];
                  const Icon = item.icon;

                  return (
                    <div key={item.key}>
                      <div
                        className={cn(
                          "group rounded-2xl border p-4 transition-all duration-200 sm:p-5",
                          completed
                            ? "border-emerald-500/20 bg-emerald-500/[0.035]"
                            : "border-border/70 bg-background hover:border-primary/30 hover:bg-muted/20",
                        )}
                      >
                        <div className="flex items-start gap-4">
                          {/* Step indicator */}
                          <div
                            className={cn(
                              "relative flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border",
                              completed
                                ? "border-emerald-500/20 bg-emerald-500/10"
                                : "border-primary/10 bg-primary/10",
                            )}
                          >
                            {completed ? (
                              <CheckCircle2 className="h-5 w-5 text-emerald-500" />
                            ) : (
                              <Icon className="h-5 w-5 text-primary" />
                            )}

                            {index < requirementItems.length - 1 && (
                              <div
                                className={cn(
                                  "absolute -bottom-6 left-1/2 hidden h-5 w-px -translate-x-1/2 sm:block",
                                  completed ? "bg-emerald-500/30" : "bg-border",
                                )}
                              />
                            )}
                          </div>

                          {/* Content */}
                          <div className="min-w-0 flex-1">
                            <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                              <div className="flex flex-wrap items-center gap-2">
                                <h3 className="font-semibold">{item.title}</h3>

                                <Badge
                                  variant="secondary"
                                  className={cn(
                                    "rounded-full text-[11px]",
                                    completed
                                      ? "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400"
                                      : "bg-amber-500/10 text-amber-600 dark:text-amber-400",
                                  )}
                                >
                                  {completed ? "Completed" : "Action required"}
                                </Badge>
                              </div>

                              {!completed && (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="w-full shrink-0 sm:w-auto"
                                  onClick={() => {
                                    if (item.key === "merchantSettings") {
                                      setShowMerchantSettings((prev) => !prev);
                                      return;
                                    }

                                    handleRequirementAction(item.route);
                                  }}
                                >
                                  {item.key === "merchantSettings" ? (
                                    <>
                                      {showMerchantSettings
                                        ? "Hide Settings"
                                        : item.actionLabel}

                                      {showMerchantSettings ? (
                                        <ChevronUp className="ml-1 h-4 w-4" />
                                      ) : (
                                        <ChevronDown className="ml-1 h-4 w-4" />
                                      )}
                                    </>
                                  ) : (
                                    <>
                                      {item.actionLabel}
                                      <ChevronRight className="ml-1 h-4 w-4" />
                                    </>
                                  )}
                                </Button>
                              )}
                            </div>

                            <p className="mt-1 text-sm leading-relaxed text-muted-foreground">
                              {completed
                                ? item.completedDescription
                                : item.description}
                            </p>
                          </div>
                        </div>
                      </div>

                      {/* Merchant Settings Dropdown */}
                      {item.key === "merchantSettings" && (
                        <div
                          className={cn(
                            "grid transition-[grid-template-rows] duration-500 ease-in-out",
                            showMerchantSettings
                              ? "grid-rows-[1fr]"
                              : "grid-rows-[0fr]",
                          )}
                        >
                          <div className="min-h-0 overflow-hidden">
                            <div className="mt-3 rounded-2xl border border-primary/20 bg-muted/10 p-2 sm:p-3">
                              <MerchantSetting userId={userId} />
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Incomplete warning */}
            {!allCompleted && firstIncompleteRequirement && (
              <div className="mt-8 rounded-2xl border border-amber-500/20 bg-amber-500/[0.06] p-4 sm:p-5">
                <div className="flex items-start gap-3">
                  <CircleAlert className="mt-0.5 h-5 w-5 shrink-0 text-amber-500" />

                  <div className="flex-1">
                    <p className="font-medium">
                      One or more requirements still need your attention
                    </p>

                    <p className="mt-1 text-sm leading-relaxed text-muted-foreground">
                      Complete{" "}
                      <span className="font-medium text-foreground">
                        {firstIncompleteRequirement.title}
                      </span>{" "}
                      and the remaining requirements to activate your P2P
                      merchant account.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* CTA */}
            <div className="mt-8 flex flex-col gap-3 sm:flex-row">
              {!allCompleted && firstIncompleteRequirement && (
                <Button
                  className="h-14 flex-1"
                  onClick={() =>
                    handleRequirementAction(firstIncompleteRequirement.route)
                  }
                >
                  {firstIncompleteRequirement.actionLabel}
                  <ChevronRight className="ml-2 h-4 w-4" />
                </Button>
              )}

              <Button
                variant="outline"
                className="h-14 sm:min-w-[180px]"
                onClick={() => navigate("/dashboard")}
              >
                Return to Dashboard
              </Button>
            </div>

            {/* Footer */}
            <div className="mt-6 border-t border-border/60 pt-5">
              <div className="flex flex-col items-center justify-center gap-2 text-center sm:flex-row">
                <LockKeyhole className="h-3.5 w-3.5 text-muted-foreground" />

                <p className="text-xs leading-relaxed text-muted-foreground">
                  Merchant access is protected by account verification and
                  security controls. Completing these requirements does not
                  guarantee approval where additional platform review is
                  required.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default P2PGate;
